import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GenericConfirmationModal, Section } from '@ydistri/ds';
import { ActiveScreenType } from '../../../common/administrationItemsLib';
import { useSelector } from 'react-redux';
import { addToast } from '@ydistri/utils';
import EntityListGrid from './EntityListGrid';
import { ReduxState, useAppDispatch } from '../../../../../store';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  EntityListUpdateRequest,
  useDeleteEntityListMutation,
  useGetEntityListImportSummaryQuery,
  useUpdateEntityListMutation,
} from '../../apiEntityLists';
import {
  setActiveScreenType,
  setImportStep,
  setSelectedEntityList,
} from '../../entityListAdministrationSlice';
import {
  EntityListTypeSupported,
  getEntityListSpecification,
  IMPORT_STEPS,
} from '../../entityListsLib';
import EntityListDetails from '../../components/EntityListDetails';
import AdministrationItemActions from './AdministrationItemActions';
import { useCurrentEntityListState } from '../../hooks/useCurrentEntityListState';
import EntityListModal from '../../modals/EntityListModal';
import EntityListItemsDownloadButton from '../../components/buttons/EntityListItemsDownloadButton';
import EntityListItemsSectionHeader from './EntityListItemsSectionHeader';
import { useUnarchiveEntityList } from '../../hooks/useUnarchiveEntityList';

interface EntityListItemsSectionProps {
  entityListType: EntityListTypeSupported;
}

const EntityListItemsSection: React.FC<EntityListItemsSectionProps> = ({ entityListType }) => {
  const dispatch = useAppDispatch();

  const entityListSpecification = getEntityListSpecification(entityListType);

  const entityListState = useCurrentEntityListState(entityListType);
  const activeScreenTypeForced = entityListState?.activeScreenTypeForced ?? false;

  const selectedEntityList = useSelector(
    (state: ReduxState) => state.entityListsAdministration[entityListType].selectedEntityList,
  );
  const showArchivedItems = useSelector(
    (state: ReduxState) => state.entityListsAdministration[entityListType].showArchivedItems,
  );
  const [isBusy, setIsBusy] = useState(false);
  const { unarchive, isLoading: isUnarchiving } = useUnarchiveEntityList();
  const [showDeleteEntityListConfirmation, setShowDeleteEntityListConfirmation] = useState(false);
  const [deleteEntityList] = useDeleteEntityListMutation();
  const [updateEntityList] = useUpdateEntityListMutation();
  const [showEditModal, setShowEditModal] = useState(false);

  const { data: entityListImportSummary, isFetching: isFetchingSummary } =
    useGetEntityListImportSummaryQuery(selectedEntityList?.entityListId ?? skipToken);

  const totalItemCount =
    (selectedEntityList
      ? selectedEntityList.departmentCount +
        selectedEntityList.skuCount +
        selectedEntityList.productCount +
        selectedEntityList.storeCount +
        selectedEntityList.storeDepartmentCount
      : 0) > 0;

  useEffect(() => {
    if (!isFetchingSummary && !activeScreenTypeForced) {
      if (entityListImportSummary && entityListImportSummary.summaries.length > 0) {
        dispatch(setActiveScreenType({ entityListType, data: ActiveScreenType.EDIT }));
        const hasItems = entityListImportSummary.summaries.find(i => i.itemCount > 0);
        if (hasItems) {
          dispatch(setImportStep({ entityListType, data: IMPORT_STEPS.ITEM_VALIDATION }));
        } else {
          dispatch(setImportStep({ entityListType, data: IMPORT_STEPS.ITEM_IMPORT }));
        }
      }
    }
  }, [
    isFetchingSummary,
    entityListImportSummary,
    // summaryLoaded,
    dispatch,
    entityListType,
    totalItemCount,
    activeScreenTypeForced,
  ]);

  /**
   * Delete Product List action handler.
   * Display a confirmation modal.
   */
  const onEntityListDelete = useCallback(() => {
    setShowDeleteEntityListConfirmation(true);
  }, []);

  /**
   * Close the confirmation modal
   */
  const closeEntityListDeleteConfirm = useCallback(() => {
    setShowDeleteEntityListConfirmation(false);
  }, []);

  const onEntityListContentEdit = useCallback(() => {
    dispatch(setActiveScreenType({ entityListType, data: ActiveScreenType.EDIT }));
    dispatch(setImportStep({ entityListType, data: IMPORT_STEPS.ITEM_IMPORT }));
  }, [dispatch, entityListType]);

  const onEntityListArchiveToggle = useCallback(() => {
    if (!selectedEntityList) {
      return;
    }

    if (selectedEntityList.isArchived) {
      void unarchive({ entityListId: selectedEntityList.entityListId, entityListType });
      return;
    }

    if (!isBusy) {
      setIsBusy(true);

      const request: EntityListUpdateRequest = {
        entityListId: selectedEntityList.entityListId,
        isArchived: !selectedEntityList.isArchived,
      };

      updateEntityList(request)
        .unwrap()
        .then(updatedEntityList => {
          if (!showArchivedItems) {
            dispatch(setSelectedEntityList({ entityListType, data: undefined }));
          } else {
            dispatch(setSelectedEntityList({ entityListType, data: updatedEntityList }));
          }

          if (updatedEntityList) {
            dispatch(
              addToast({
                message: `${entityListSpecification.title} was ${
                  updatedEntityList.isArchived ? 'archived' : 'unarchived'
                }`,
              }),
            );
          }
        })
        // eslint-disable-next-line @typescript-eslint/use-unknown-in-catch-callback-variable -- we know it's an error
        .catch((error: Error) => {
          dispatch(
            addToast({
              isError: true,
              message: `${entityListSpecification.title}'s archive status was not changed`,
              description: error.message,
            }),
          );
        })
        .finally(() => {
          setIsBusy(false);
        });
    }
  }, [
    selectedEntityList,
    isBusy,
    entityListType,
    updateEntityList,
    showArchivedItems,
    dispatch,
    entityListSpecification.title,
    unarchive,
  ]);

  const onProductListEdit = useCallback(() => setShowEditModal(true), []);
  const onEditProductListModalClose = useCallback(() => setShowEditModal(false), []);

  const onEntityListDeleteConfirm = useCallback(() => {
    closeEntityListDeleteConfirm();

    if (selectedEntityList) {
      setIsBusy(true);
      if (!isBusy) {
        deleteEntityList({ entityListId: selectedEntityList.entityListId, entityListType })
          .unwrap()
          .then(() => {
            dispatch(setSelectedEntityList({ entityListType, data: undefined }));
          })
          // eslint-disable-next-line @typescript-eslint/use-unknown-in-catch-callback-variable -- we know it's an error
          .catch((error: Error) => {
            dispatch(
              addToast({
                isError: true,
                message: `${entityListSpecification.title} was not deleted due to an error`,
                description: error.message,
              }),
            );
          })
          .finally(() => {
            setIsBusy(false);
          });
      }
    }
  }, [
    closeEntityListDeleteConfirm,
    deleteEntityList,
    dispatch,
    entityListSpecification.title,
    entityListType,
    isBusy,
    selectedEntityList,
  ]);

  /**
   * Actions for the header of the section
   */
  const headerActions = useMemo(() => {
    if (selectedEntityList) {
      return (
        <AdministrationItemActions
          entityListId={selectedEntityList.entityListId}
          entityListType={entityListType}
          entityListSourceType={selectedEntityList.entityListSourceTypeId}
          itemName={entityListSpecification.title}
          editableContent={selectedEntityList.isEditable}
          deletable={selectedEntityList.isDeletable}
          archivable={selectedEntityList.isArchivable}
          archived={selectedEntityList.isArchived}
          onDelete={onEntityListDelete}
          onToggleArchived={onEntityListArchiveToggle}
          onContentEdit={onEntityListContentEdit}
          onEdit={onProductListEdit}
          disabled={isBusy || isUnarchiving}
        >
          <EntityListItemsDownloadButton
            entityListId={selectedEntityList.entityListId}
            disabled={isBusy}
          />
        </AdministrationItemActions>
      );
    }
  }, [
    entityListType,
    selectedEntityList,
    entityListSpecification.title,
    onEntityListDelete,
    onEntityListArchiveToggle,
    onEntityListContentEdit,
    onProductListEdit,
    isBusy,
    isUnarchiving,
  ]);

  if (selectedEntityList) {
    return (
      <>
        <Section
          header={
            <EntityListItemsSectionHeader
              title={selectedEntityList.name}
              entityListSourceType={selectedEntityList.entityListSourceTypeId}
              archived={selectedEntityList.isArchived}
            />
          }
          headerActions={headerActions}
        >
          <EntityListDetails entityList={selectedEntityList} />
          <EntityListGrid
            selectedEntityListId={selectedEntityList.entityListId}
            entityListType={entityListType}
          />
        </Section>
        {showDeleteEntityListConfirmation && (
          <GenericConfirmationModal
            data-testid="DeleteEntityListConfirmationDialog"
            title={`Delete ${entityListSpecification.title}`}
            message={`${entityListSpecification.title} '${
              selectedEntityList.name ?? '-no title-'
            }' will be deleted. Continue?`}
            onConfirmed={onEntityListDeleteConfirm}
            onCanceled={closeEntityListDeleteConfirm}
          />
        )}
        {showEditModal && (
          <EntityListModal
            entityListType={entityListType}
            entityList={selectedEntityList}
            onClose={onEditProductListModalClose}
          />
        )}
      </>
    );
  }
};

export default EntityListItemsSection;
